import { useMemo, useState, useCallback } from 'react';
import { SolveRecord } from './SpeedStatsPanel';
import { detectTopic, TOPIC_STYLE } from '../topicUtils';

interface Props {
  show: boolean;
  onClose: () => void;
  solveRecords: SolveRecord[];
  streak: number;
}

export default function SessionSummary({ show, onClose, solveRecords, streak }: Props) {
  const [copied, setCopied] = useState(false);

  const stats = useMemo(() => {
    if (solveRecords.length === 0) return null;
    const successful = solveRecords.filter(r => r.success);
    const timed = solveRecords.filter(r => r.timeSec > 0);
    const avgTime = timed.length > 0 ? timed.reduce((s, r) => s + r.timeSec, 0) / timed.length : null;
    const bestTime = timed.length > 0 ? Math.min(...timed.map(r => r.timeSec)) : null;
    const bestRecord = bestTime != null ? timed.find(r => r.timeSec === bestTime) : null;

    const topicCounts: Record<string, number> = {};
    for (const r of solveRecords) {
      const t = detectTopic(r.query);
      topicCounts[t] = (topicCounts[t] ?? 0) + 1;
    }
    const topTopics = Object.entries(topicCounts).sort((a, b) => b[1] - a[1]);
    const mostSolvedTopic = topTopics[0]?.[0] ?? 'General';

    const today = new Date().toDateString();
    const todayCount = solveRecords.filter(r => new Date(r.timestamp).toDateString() === today).length;
    const successRate = Math.round((successful.length / solveRecords.length) * 100);

    return { total: solveRecords.length, successRate, avgTime, bestTime, bestRecord, topTopics, mostSolvedTopic, todayCount };
  }, [solveRecords]);

  const handleShare = useCallback(() => {
    if (!stats) return;
    const lines = [
      '🧠 Genius Math — Session Summary',
      '─────────────────────────────',
      `📊 Solves this session: ${stats.todayCount}`,
      `✅ Success rate: ${stats.successRate}%`,
      ...(stats.avgTime != null ? [`⏱ Avg solve time: ${stats.avgTime.toFixed(1)}s`] : []),
      ...(stats.bestTime != null ? [`🏆 Best time: ${stats.bestTime.toFixed(1)}s`] : []),
      `🔥 Current streak: ${streak} day${streak !== 1 ? 's' : ''}`,
      `📚 Top topics: ${stats.topTopics.slice(0, 4).map(([t, n]) => `${t} (${n})`).join(', ')}`,
      '─────────────────────────────',
      'Powered by Genius Math',
    ];
    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [stats, streak]);

  if (!show || !stats) return null;

  const maxTopicCount = stats.topTopics[0]?.[1] ?? 1;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">

        {/* Header */}
        <div className="bg-gradient-to-r from-violet-600 via-fuchsia-600 to-pink-600 px-6 py-5 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="absolute rounded-full bg-white"
                style={{ width: 40 + i * 20, height: 40 + i * 20, top: `${10 + i * 8}%`, left: `${i * 12}%`, opacity: 0.3 }} />
            ))}
          </div>
          <div className="relative flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-2xl">🎓</span>
                <h2 className="text-white font-black text-xl">Session Complete!</h2>
              </div>
              <p className="text-violet-100 text-sm">
                {stats.todayCount} problems solved today
                {streak > 0 ? ` · 🔥 ${streak}-day streak` : ''}
              </p>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/20 hover:bg-white/30 text-white cursor-pointer flex-shrink-0 mt-0.5">
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
        </div>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">

          {/* Key Stats Row */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { icon: 'ri-check-double-line', label: 'Success Rate', value: `${stats.successRate}%`, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/20' },
              ...(stats.avgTime != null ? [{ icon: 'ri-timer-line', label: 'Avg Time', value: `${stats.avgTime.toFixed(1)}s`, color: 'text-sky-600 dark:text-sky-400', bg: 'bg-sky-50 dark:bg-sky-900/20' }] : [{ icon: 'ri-book-open-line', label: 'Total Solves', value: String(stats.total), color: 'text-violet-600 dark:text-violet-400', bg: 'bg-violet-50 dark:bg-violet-900/20' }]),
              ...(stats.bestTime != null ? [{ icon: 'ri-trophy-line', label: 'Best Time', value: `${stats.bestTime.toFixed(1)}s`, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/20' }] : [{ icon: 'ri-fire-line', label: 'Streak', value: `${streak}d`, color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-900/20' }]),
            ].map(s => (
              <div key={s.label} className={`${s.bg} rounded-xl p-3 text-center`}>
                <div className={`w-7 h-7 flex items-center justify-center mx-auto mb-1`}>
                  <i className={`${s.icon} ${s.color} text-base`}></i>
                </div>
                <div className={`font-black text-lg ${s.color}`}>{s.value}</div>
                <div className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Best solve */}
          {stats.bestRecord && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-amber-500">🏆</span>
                <span className="text-xs font-bold text-amber-700 dark:text-amber-400">Fastest Solve This Session</span>
              </div>
              <code className="text-xs text-slate-600 dark:text-slate-300 font-mono line-clamp-1">{stats.bestRecord.query}</code>
              <span className="text-xs text-amber-600 dark:text-amber-400 font-bold">{stats.bestRecord.timeSec.toFixed(2)}s</span>
            </div>
          )}

          {/* Topic Distribution */}
          <div>
            <h3 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2.5">Topics Covered</h3>
            <div className="space-y-2">
              {stats.topTopics.map(([topic, count]) => {
                const style = TOPIC_STYLE[topic] ?? TOPIC_STYLE.General;
                const pct = Math.round((count / maxTopicCount) * 100);
                return (
                  <div key={topic} className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${style.badge} ${style.badgeText} whitespace-nowrap w-32 text-center flex-shrink-0`}>
                      {topic}
                    </span>
                    <div className="flex-1 bg-slate-100 dark:bg-slate-700 rounded-full h-2">
                      <div
                        className="h-2 rounded-full transition-all duration-700"
                        style={{ width: `${pct}%`, backgroundColor: style.dot }}
                      />
                    </div>
                    <span className="text-[10px] text-slate-500 dark:text-slate-400 font-bold w-6 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Motivational message */}
          <div className="bg-gradient-to-r from-violet-50 to-fuchsia-50 dark:from-violet-900/20 dark:to-fuchsia-900/20 border border-violet-100 dark:border-violet-800/40 rounded-xl p-3 text-center">
            <p className="text-sm text-violet-700 dark:text-violet-300 font-semibold">
              {stats.successRate >= 80 ? '🌟 Excellent work! You\'re on fire.' : stats.successRate >= 60 ? '💪 Solid session! Keep going.' : '📈 Good effort — every problem counts!'}
            </p>
            <p className="text-xs text-violet-500 dark:text-violet-400 mt-0.5">
              Your strongest topic today: <strong>{stats.mostSolvedTopic}</strong>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 flex gap-2">
          <button
            onClick={handleShare}
            className="flex items-center gap-1.5 px-4 py-2.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className={`${copied ? 'ri-check-line text-emerald-500' : 'ri-share-line'} text-sm`}></i>
            {copied ? 'Copied!' : 'Copy Summary'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 py-2.5 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:opacity-90 text-white text-sm font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap"
          >
            Keep Solving
          </button>
        </div>
      </div>
    </div>
  );
}
